import type { Theme } from "@mui/material/styles";
import { createStyles, makeStyles } from "@mui/styles";
import { ApexOptions } from "apexcharts";
import { AreaData, DeepPartial, LayoutOptions, LineData } from "lightweight-charts";
import React from "react";
import Chart from "react-apexcharts";
import { ICompareOptions } from "../defaults";
import { formatChartPrice, formattedDateAndTime } from "../helpers";
import { useUserSlice } from "src/store/features/user.slice";

export const TooltipStyles = makeStyles((theme: Theme) =>
  createStyles({
    tooltip: {
      width: "99%",
      "& .apexcharts-tooltip": {
        zIndex: 200, // ensure tooltip is above time period filter and compare options
      },
      // used to change the size of the coloured markers in the tooltip
      "& .apexcharts-tooltip-marker": {
        width: "6px",
        height: "6px",
      },

      "& .tooltip-container": {
        fontSize: "12px",
        fontFamily: "Söhne",
        pointerEvents: "none",
        color: "#131722",
        display: "flex",
        padding: "16px",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "flex-start",
        gap: "12px",
        borderRadius: "8px",
        backgroundColor: "#FFF",
        /* Shadow (XL)/Standard */
        boxShadow: " 0px 12px 24px 0px rgba(216, 216, 218, 0.80)",
      },
      "& .content-container": {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "flex-start",
        gap: "8px",
        height: "auto",
      },
      "& .text": {
        fontSize: "12px",
      },
      "& .circle": {
        width: "6px",
        height: "6px",
        borderRadius: "6px",
        border: "1px solid #FFF",
      },
      "& .bitcoin .circle": {
        background: "#CCC3AE",
      },
      "& .ethereum .circle": {
        background: "#005790",
      },
      "& .bitcoin": {
        display: "flex",
        alignItems: "center",
        gap: "4px",
        alignSelf: "stretch",
      },
      "& .ethereum": {
        display: "flex",
        alignItems: "center",
        gap: "4px",
        alignSelf: "stretch",
      },

      "& .datetime": {
        color: "#BDC2C5",
        fontSize: "12px",
        lineHeight: "14px",
      },
    },
  }),
);

interface BalanceChartProps {
  data: AreaData[];
  bitcoinData?: LineData[];
  ethereumData?: LineData[];
  compareOptions?: ICompareOptions[];
  colors?: DeepPartial<LayoutOptions>;
  timeFilterOptions?: string[];
}

const BalanceChart = (props: BalanceChartProps) => {
  const { data, bitcoinData, ethereumData, compareOptions } = props;

  const {
    defaultCurrency: { code },
  } = useUserSlice();

  const areaTopColor = "#F8A0A0";
  const areaBottomColor = "#FFF";
  const btcColor = "#CCC3AE";
  const ethColor = "#005790";
  const btcVisible = compareOptions ? compareOptions[0].checked : false;
  const ethVisible = compareOptions ? compareOptions[1].checked : false;

  const dataValues = data.map((el: AreaData): number => el.value);
  const dataTimes = data.map((el: AreaData): string => {
    const dateTime = formattedDateAndTime(el.time as number);
    return dateTime;
  });

  const btcDataValues = bitcoinData?.map((el: LineData): number => el.value);
  const ethDataValues = ethereumData?.map((el: LineData): number => el.value);
  const dataBTCTimes = bitcoinData?.map((el: AreaData): string => {
    const dateTime = formattedDateAndTime(el.time as number);
    return dateTime;
  });

  const options: ApexOptions = {
    colors: ["#FC4C4C", btcColor, ethColor],
    chart: {
      type: "area",
      toolbar: { show: false },
      sparkline: {
        enabled: true,
      },
      zoom: {
        enabled: true,
        type: "x",
        autoScaleYaxis: true,
      },
    },
    stroke: {
      show: true,
      curve: "smooth",
      colors: ["#fcd0d0", btcColor, ethColor],
      width: 1,
    },
    fill: {
      type: "gradient",
      colors: [areaTopColor],
      gradient: {
        shade: "light",
        type: "vertical",
        gradientToColors: [areaBottomColor],
        shadeIntensity: 1,
        opacityFrom: 1,
        opacityTo: 1,
        inverseColors: false,
        stops: [0, 100],
      },
    },
    labels: dataTimes.length > 0 ? dataTimes : dataBTCTimes,
    markers: {
      size: 0,
    },
    dataLabels: { enabled: false },
    grid: { show: false },
    xaxis: { tooltip: { enabled: false } },
    yaxis: { show: false, tooltip: { enabled: false } },
    legend: {
      show: false,
    },
    tooltip: {
      shared: true,
      intersect: false,
      marker: { show: true },
      x: { show: false },
      y: {
        formatter: function (y) {
          if (typeof y !== "undefined") {
            return y.toFixed(4);
          }
          return y;
        },
      },
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        const date = w.globals.categoryLabels[dataPointIndex];

        let portfolioPrice = null;
        if (series[0] && series[0][dataPointIndex]) {
          portfolioPrice = formatChartPrice(series[0][dataPointIndex], code);
        }
        formatChartPrice(series[0][dataPointIndex], code);
        let btcPrice = null;
        if (series[1] && series[1][dataPointIndex]) {
          btcPrice = formatChartPrice(series[1][dataPointIndex], code);
        }

        let ethPrice = null;
        if (series[2] && series[2][dataPointIndex]) {
          ethPrice = formatChartPrice(series[2][dataPointIndex], code);
        }

        // Start tooltip container
        var tooltipHTML =
          '<div class="tooltip-container">' +
          // Start content container
          '<div class="content-container">';

        if (portfolioPrice) {
          // Portfolio Price
          tooltipHTML += '<div class="price">Portfolio: ' + portfolioPrice + "</div>";
        }

        // Check if btcPrice exists before rendering Bitcoin Element
        if (btcPrice) {
          tooltipHTML += `<div class="bitcoin">
          <div class="circle"></div>
          <div class="text">Bitcoin: ${btcPrice}</div>
          </div>`;
        }

        // Check if ethPrice exists before rendering Ethereum Element
        if (ethPrice) {
          tooltipHTML += `<div class="ethereum">
          <div class="circle"></div>
          <div class="text">Ethereum: ${ethPrice}</div>
          </div>`;
        }

        // Continue with Ethereum Element and other parts
        tooltipHTML +=
          // Datetime Element
          '<div class="datetime">' +
          date +
          "</div>" +
          // End Datetime Element
          "</div>"; // End tooltip container

        return tooltipHTML;
      },
    },
  };

  const series = React.useMemo(() => {
    const btcData = btcVisible && btcDataValues ? btcDataValues : [];
    const ethData = ethVisible && ethDataValues ? ethDataValues : [];

    return [
      {
        name: "Portfolio",
        type: "area",
        data: dataValues,
      },
      {
        name: "Bitcoin",
        type: "line",
        data: btcData,
      },
      {
        name: "Ethereum",
        type: "line",
        data: ethData,
      },
    ];
  }, [dataValues, btcVisible, btcDataValues, ethVisible, ethDataValues]);

  return (
    <Chart
      id="balance-chart"
      className={TooltipStyles().tooltip}
      height={"120px"}
      width={"100%"}
      options={options}
      series={series}
    />
  );
};

export default BalanceChart;
